import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BookOpen, Lock } from 'lucide-react'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { Button } from '@/components/ui/button'
import { RouteSkeleton } from '@/components/ui/loader'
import { OutcomeCard } from '@/components/ui/outcome-card'
import { BentoCard } from '@/components/ui/bento'

/**
 * The two states a Pro gate needs — frame 21d: «/catalogue trenger TO
 * tilstander, ikke én.»
 *
 * They live here rather than in `routes/index.tsx` because that file exports
 * `router`, so every component defined beside it trips
 * `react-refresh/only-export-components`.
 */

/** «VENTER PÅ NIVÅ · Skjelett, ikke blank skjerm.» The route's own placeholder, so a gated page
 *  draws one shape while the guard decides (sweep 24.10.2026: it was a six-tile grid nobody gets). */
export function ProGateSkeleton() {
  return <RouteSkeleton />
}

/**
 * «IKKE PRO · Leverandørkatalogen er i Pro · Søk i hoteller, transport og
 * leverandører du har brukt før · Se Pro · Tilbake».
 *
 * A state, not a redirect. Phase 8 had already moved the bounce from
 * `/dashboard` to `/pricing` — the right direction, per 22b «det er slik en
 * oppgradering selges, ikke ved å skjule den» — but any redirect throws away
 * the thing the user asked for. Naming the feature in place sells it; being
 * moved somewhere else reads as the page being broken.
 */
export function CatalogueProGate() {
  const { t } = useTranslation()
  return (
    <BentoCard as="section" roomy className="mx-auto max-w-[var(--content-width)]">
      {/* In a card: a closed tool is still a place in the app, not text floating on the page (Parley pass 24.10.2026). */}
      <OutcomeCard
        icon={BookOpen}
        tone="info"
        title={t('catalogue.proTitle')}
        description={t('catalogue.proDescription')}
      >
        <Button asChild>
          <Link to="/pricing">{t('catalogue.proSeePro')}</Link>
        </Button>
        <Button variant="secondary" asChild>
          <Link to="/dashboard">{t('common.back')}</Link>
        </Button>
      </OutcomeCard>
    </BentoCard>
  )
}

export type AccessGateTool = 'communication' | 'offers' | 'offersRole' | 'destinations' | 'presentations'

/**
 * A surface the viewer may not open, said IN PLACE (03.10.2026 re-score).
 *
 * The four guards used to `<Navigate replace>` to /events or /dashboard: the address changed, Back
 * could not return, and nothing said why, so a shared link read as broken. Same answer as
 * `CatalogueProGate` above: name the surface where it was asked for, say why it is closed, and
 * offer the nearest place that IS theirs. The URL stays, so the reason is still on screen if
 * they send it to someone who can help.
 */
export function AccessGate({ tool, action }: {
  tool: AccessGateTool
  /** The way to the switch the text names (remaining-surfaces review 25.10.2026: «slå den på under
   *  Innstillinger» beside buttons that went to /events and /dashboard). */
  action?: { to: string; label: string }
}) {
  const { t } = useTranslation()
  useDocumentTitle(t(`accessGate.${tool}.title`))
  return (
    <BentoCard as="section" roomy className="mx-auto max-w-[var(--content-width)]">
      {/* In a card: a closed tool is still a place in the app, not text floating on the page (Parley pass 24.10.2026). */}
      <OutcomeCard icon={Lock} tone="info" title={t(`accessGate.${tool}.title`)} description={t(`accessGate.${tool}.body`)}>
        {action ? (
          <>
            <Button asChild>
              <Link to={action.to}>{action.label}</Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/events">{t('accessGate.toEvents')}</Link>
            </Button>
          </>
        ) : (
          <>
            <Button asChild>
              <Link to="/events">{t('accessGate.toEvents')}</Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/dashboard">{t('accessGate.toDashboard')}</Link>
            </Button>
          </>
        )}
      </OutcomeCard>
    </BentoCard>
  )
}
